import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { FichaService } from '../../../ficha-veiculo.service';
import { VeiculoEmSeuNome } from '../../model/veiculo';
import { ModalMessageComponent } from './../../../../shared/modais/modal-message/modal-message.component';

@Component({
  selector: 'app-ficha-cadastro-veiculo-proprio-add',
  templateUrl: './ficha-cadastro-veiculo-proprio-add.component.html',
  styleUrls: ['./ficha-cadastro-veiculo-proprio-add.component.scss'],
})
export class FichaCadastroVeiculoProprioAddComponent
  implements OnInit, OnDestroy {
  @Input() index: number;
  @Input() veiculo: VeiculoEmSeuNome;
  @Input() veiculos: VeiculoEmSeuNome[];
  @Output() remover: EventEmitter<void> = new EventEmitter();
  @Output() adicionar: EventEmitter<void> = new EventEmitter();
  @Output() salvar: EventEmitter<VeiculoEmSeuNome> = new EventEmitter();

  @ViewChild('modalMessage', { static: true })
  modalMessage: ModalMessageComponent;

  form: FormGroup;

  listaTipoVeiculo = [];
  listaMarca = [];
  listaAnoModelo = [];
  listaModelo = [];
  listaVersao = [];

  isSelectedTipoVeiculo = false;
  isSelectedMarca = false;
  isSelectedAnoModelo = false;
  isSelectedModelo = false;
  isSelectedVersao = false;
  isSelectedCotacao = false;

  isLoadingTipoVeiculo = false;
  isLoadingMarca = false;
  isLoadingAnoModelo = false;
  isLoadingModelo = false;
  isLoadingVersao = false;
  isLoadingCotacao = false;

  private isAlive = false;

  private isSelectedUfLicenciamento = false;

  private codigoFornecedorCotacao: string;
  private isAlteracao = false;

  private subsTipoVeiculo: Subscription;
  private subsMarca: Subscription;
  private subsModelo: Subscription;
  private subsVersao: Subscription;
  private subsCotacao: Subscription;

  isSelectedMarcaAfterBlur = true;
  isSelectedAnoModeloAfterBlur = true;
  isSelectedModeloAfterBlur = true;
  isSelectedVersaoAfterBlur = true;

  constructor(
    private formBuilder: FormBuilder,
    private fichaService: FichaService
  ) {}

  ngOnInit() {
    this.buildForm();
    this.loadListaTipoVeiculo();
    this.patchForm();
  }

  ngOnDestroy() {
    if (this.subsTipoVeiculo) {
      this.subsTipoVeiculo.unsubscribe();
    }
    if (this.subsMarca) {
      this.subsMarca.unsubscribe();
    }
    if (this.subsModelo) {
      this.subsModelo.unsubscribe();
    }
    if (this.subsVersao) {
      this.subsVersao.unsubscribe();
    }
    if (this.subsCotacao) {
      this.subsCotacao.unsubscribe();
    }
    this.isAlive = false;
  }

  private buildForm(): void {
    this.form = this.formBuilder.group({
      veiculoPlaca: ['', [this.placaDuplicadaValidator()]],
      isNaoSeiPlaca: [false],
      veiculoCategoria: [''],
      veiculoMarca: [''],
      veiculoAno: [''],
      veiculoModelo: [''],
      veiculoVersao: [''],
    });
  }

  private patchForm(): void {
    if (this.veiculo) {
      this.isAlteracao = true;
      this.form.patchValue(this.veiculo);
      if (this.veiculo.isNaoSeiPlaca) {
        this.loadListaMarca();
        this.onChangeMarca(null);
        this.onChangeAnoModelo(null);
        this.onChangeModelo(null);
      }
    }
  }

  loadListaTipoVeiculo() {
    this.clearList(['TipoVeiculo', 'Marca', 'AnoModelo', 'Modelo', 'Versao']);
    this.isLoadingTipoVeiculo = true;
    this.subsTipoVeiculo = this.fichaService
      .getTipoVeiculo()
      .pipe(
        take(1),
        finalize(() => {
          this.isLoadingTipoVeiculo = false;
        })
      )
      .subscribe((res) => {
        if (res.tipos) {
          res.tipos.forEach((tipo) => {
            if ([4].includes(tipo.id)) {
              this.listaTipoVeiculo.push(tipo);
            }
          });
        }
        this.resetAlteracao();
      });
  }

  loadListaMarca() {
    this.clearList(['Marca', 'AnoModelo', 'Modelo', 'Versao', 'Cotacao']);
    this.isLoadingMarca = true;
    this.subsMarca = this.fichaService
      .getMarcaPorTipo(this.veiculoCategoria)
      .pipe(
        take(1),
        finalize(() => {
          this.isLoadingMarca = false;
        })
      )
      .subscribe((res) => {
        this.listaMarca = res.marcas;
        if (this.isAlteracao && this.veiculo && this.veiculo.veiculoMarca) {
          const veiculoMarca = this.listaMarca.find(f => f.id === this.veiculo.veiculoMarca.id);
          this.form.get('veiculoMarca').setValue(veiculoMarca);
        }
        this.resetAlteracao();
      });
  }

  loadListaAnoModelo() {
    this.clearList(['AnoModelo', 'Modelo', 'Versao', 'Cotacao']);

    this.isLoadingAnoModelo = true;
    this.fichaService
      .getAnoModeloPorMarca(this.veiculoCategoria, this.veiculoMarca)
      .pipe(
        take(1),
        finalize(() => {
          this.isLoadingAnoModelo = false;
        })
      )
      .subscribe(
        (res) => this.handleListaAnoModelo(res.anosModelo),
        (error) => {
          this.modalMessage.show(
            'Não há ano de veículo para as características selecionadas.'
          );
        }
      );
  }

  loadListaModelo() {
    this.clearList(['Modelo', 'Versao', 'Cotacao']);
    this.isLoadingModelo = true;
    this.subsModelo = this.fichaService
      .getModeloPorMarcaAno(
        this.veiculoCategoria,
        this.veiculoMarca,
        this.veiculoAno
      )
      .pipe(
        take(1),
        finalize(() => {
          this.isLoadingModelo = false;
        })
      )
      .subscribe(
        (res) => {
          this.listaModelo = res.modelos;
          if (this.isAlteracao && this.veiculo && this.veiculo.veiculoModelo) {
            const veiculoModelo = this.listaModelo.find(f => f.id === this.veiculo.veiculoModelo.id);
            this.form.get('veiculoModelo').setValue(veiculoModelo);
          }
          this.resetAlteracao();
        },
        () =>
          this.modalMessage.show(
            ' Não há modelo de veículo para as características selecionadas.'
          )
      );
  }

  loadListaVersao() {
    this.clearList(['Versao', 'Cotacao']);
    this.isLoadingVersao = true;
    this.subsVersao = this.fichaService
      .getVersaoPorMarcaModeloAno(
        this.veiculoCategoria,
        this.veiculoMarca,
        this.veiculoAno,
        this.veiculoModelo
      )
      .pipe(
        take(1),
        finalize(() => {
          this.isLoadingVersao = false;
        })
      )
      .subscribe(
        (res) => {
          this.listaVersao = res.versoes;
          if (this.isAlteracao && this.veiculo && this.veiculo.veiculoVersao) {
            const veiculoVersao = this.listaVersao.find(f => f.id === this.veiculo.veiculoVersao.id);
            this.form.get('veiculoVersao').setValue(veiculoVersao);
          }
          this.resetAlteracao();
        },
        (error) => {}
      );
  }

  onChangeMarca(event) {
    this.isSelectedMarca = true;
    this.loadListaAnoModelo();
    this.onSelectedMarcaChange();
  }

  onChangeAnoModelo(event) {
    this.isSelectedAnoModelo = true;
    this.loadListaModelo();
    this.onSelectedAnoModeloChange();
  }

  onChangeModelo(event) {
    this.isSelectedModelo = true;
    this.loadListaVersao();
    this.onSelectedModeloChange();
  }

  onChangeVersao(event) {
    this.codigoFornecedorCotacao = this.listaVersao[event.target.selectedIndex - 1].codigoMolicar;
    this.isSelectedVersao = true;
  }

  onBlurMarca(event) {
    this.isSelectedMarcaAfterBlur = event.target.selectedIndex > 0;
  }

  onBlurAnoModelo(event) {
    this.isSelectedAnoModeloAfterBlur = event.target.selectedIndex > 0;
  }

  onBlurModelo(event) {
    this.isSelectedModeloAfterBlur = event.target.selectedIndex > 0;
  }

  onBlurVersao(event) {
    this.isSelectedVersaoAfterBlur = event.target.selectedIndex > 0;
  }

  private handleListaAnoModelo(anos) {
    this.listaAnoModelo = anos;
    this.resetAlteracao();
  }

  clearList(lista: string[]) {
    if (!this.isAlteracao && lista && lista.length) {
      lista.forEach((item) => {
        switch (item) {
          case 'TipoVeiculo': {
            this.listaTipoVeiculo = [];
            this.isSelectedTipoVeiculo = false;
            this.form.get('veiculoCategoria').setValue(null);
            this.onSelectedTipoVeiculoChange();
            break;
          }
          case 'Marca': {
            this.listaMarca = [];
            this.isSelectedMarca = false;
            this.form.get('veiculoMarca').setValue(null);
            this.onSelectedMarcaChange();
            break;
          }
          case 'AnoModelo': {
            this.listaAnoModelo = [];
            this.isSelectedAnoModelo = false;
            this.form.get('veiculoAno').setValue(null);
            this.onSelectedAnoModeloChange();
            break;
          }
          case 'Modelo': {
            this.listaModelo = [];
            this.isSelectedModelo = false;
            this.form.get('veiculoModelo').setValue(null);
            this.onSelectedModeloChange();
            break;
          }
          case 'Versao': {
            this.listaVersao = [];
            this.isSelectedVersao = false;
            this.form.get('veiculoVersao').setValue(null);
            break;
          }
        }
      });
      this.isSelectedMarcaAfterBlur = true;
      this.isSelectedAnoModeloAfterBlur = true;
      this.isSelectedModeloAfterBlur = true;
      this.isSelectedVersaoAfterBlur = true;
    }
  }

  private placaDuplicadaValidator(): ValidatorFn {
    return (control: AbstractControl): Validators | null => {
      const value = control.value;

      if (
        value &&
        this.veiculos.some(
          (v) => v.veiculoPlaca === value && v.isEdit !== this.veiculo.isEdit
        )
      ) {
        return { duplicidade: true };
      }

      return null;
    };
  }

  resetAlteracao() {
    if (
      !this.isLoadingTipoVeiculo &&
      !this.isLoadingMarca &&
      !this.isLoadingAnoModelo &&
      !this.isLoadingModelo &&
      !this.isLoadingVersao &&
      !this.isLoadingCotacao
    ) {
      this.isAlteracao = false;
    }
  }

  public get veiculoPlaca(): string {
    return (
      this.form &&
      this.form.get('veiculoPlaca') &&
      this.form.get('veiculoPlaca').value
    );
  }

  public get veiculoCategoria(): string {
    return (
      this.form &&
      this.form.get('veiculoCategoria') &&
      this.form.get('veiculoCategoria').value
    );
  }

  public get veiculoMarca(): string {
    return (
      this.form &&
      this.form.get('veiculoMarca') &&
      this.form.get('veiculoMarca').value &&
      this.form.get('veiculoMarca').value.id
    );
  }

  public get veiculoAno(): string {
    return (
      this.form &&
      this.form.get('veiculoAno') &&
      this.form.get('veiculoAno').value
    );
  }

  public get veiculoModelo(): string {
    return (
      this.form &&
      this.form.get('veiculoModelo') &&
      this.form.get('veiculoModelo').value &&
      this.form.get('veiculoModelo').value.id
    );
  }

  public get veiculoVersao(): string {
    return (
      this.form &&
      this.form.get('veiculoVersao') &&
      this.form.get('veiculoVersao').value &&
      this.form.get('veiculoVersao').value.id
    );
  }

  public get formValid(): boolean {
    const {
      isNaoSeiPlaca,
      veiculoCategoria,
      veiculoMarca,
      veiculoAno,
      veiculoModelo,
      veiculoVersao,
    } = this.form.getRawValue();

    if (isNaoSeiPlaca) {
      return (
        veiculoCategoria &&
        veiculoMarca &&
        veiculoAno &&
        veiculoModelo &&
        veiculoVersao
      );
    }

    return this.form.get('veiculoPlaca').valid;
  }

  public get isNaoSeiPlaca(): boolean {
    return (
      this.form &&
      this.form.get('isNaoSeiPlaca') &&
      this.form.get('isNaoSeiPlaca').value
    );
  }

  public onClickCancelar(): void {
    this.remover.emit();
  }

  public onClickSalvar(): void {
    this.salvar.emit(this.form.getRawValue());
    this.form.reset();
  }

  public get title(): string {
    return `Informações do Caminhão Próprio ${this.index + 1}`;
  }

  public onClickNaoSeiPlaca(): void {
    this.form.get('veiculoPlaca').setValue('');

    if (this.isNaoSeiPlaca) {
      this.form.get('veiculoPlaca').disable();
      this.isSelectedTipoVeiculo = true;
      this.form.get('veiculoCategoria').setValue('4');
      this.form.get('veiculoCategoria').disable();
      this.loadListaMarca();
      this.onSelectedTipoVeiculoChange();
      return;
    }
    this.form.get('veiculoPlaca').enable();
  }

  public get showCancel(): boolean {
    return (
      (this.veiculos.length === 1 && this.veiculo.isEdit) ||
      this.veiculos.length > 1
    );
  }

  onSelectedTipoVeiculoChange() {
    if (this.isSelectedTipoVeiculo) {
      this.form.get('veiculoMarca')?.enable();
    }
    else {
      this.form.get('veiculoMarca')?.disable();
    }
  }

  onSelectedMarcaChange() {
    if (this.isSelectedMarca) {
      this.form.get('veiculoAno')?.enable();
    }
    else {
      this.form.get('veiculoAno')?.disable();
    }
  }

  onSelectedAnoModeloChange() {
    if (this.isSelectedAnoModelo) {
      this.form.get('veiculoModelo')?.enable();
    }
    else {
      this.form.get('veiculoModelo')?.disable();
    }
  }

  onSelectedModeloChange() {
    if (this.isSelectedModelo) {
      this.form.get('veiculoVersao')?.enable();
    }
    else {
      this.form.get('veiculoVersao')?.disable();
    }
  }
}
